import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Info, Banknote, DoorOpen, Trash2, Pencil, ChevronLeft, ChevronRight, CalendarDays, Users, Receipt, Plus, Share2, Copy } from "lucide-react";

const EXPENSE_CATS = ["Lessive / Produits", "Salaire", "Transport", "Loyer / Charges", "Autre"];

export default function CashClosing() {
  const { user, settings } = useAuth();
  const { confirm } = useConfirm();
  const cur = settings?.currency || "KMF";
  const isAdmin = user?.role === "admin";
  const todayStr = new Date().toISOString().slice(0, 10);
  const [selDate, setSelDate] = useState(todayStr);
  const [summary, setSummary] = useState({ opened: false, opening_float: 0, expected_cash: 0, expected_in_drawer: 0, total_collected: 0, by_method: [], by_agent: [], cash_payments: [], existing_closing: null });
  const [openingInput, setOpeningInput] = useState("");
  const [declared, setDeclared] = useState("");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [editRow, setEditRow] = useState(null);
  const [editDeclared, setEditDeclared] = useState("");
  const [editNote, setEditNote] = useState("");
  const [outstanding, setOutstanding] = useState({ orders: [], total_remaining: 0, count: 0 });
  const [methods, setMethods] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("");

  const isToday = selDate === todayStr;
  const dateLabel = isToday ? "aujourd'hui" : new Date(selDate + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const load = useCallback(async () => {
    try {
      const sum = await api.get("/cash-closings/summary", { params: { date: selDate } });
      setSummary(sum.data);
      const ex = sum.data.existing_closing;
      setDeclared(ex ? String(ex.declared_cash ?? "") : "");
      setNote(ex ? (ex.note || "") : "");
      const h = await api.get("/cash-closings");
      setHistory(h.data);
      const out = await api.get("/cash-closings/outstanding");
      setOutstanding(out.data);
      const m = await api.get("/payment-methods", { params: { active_only: true } });
      setMethods(m.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [selDate]);
  useEffect(() => { load(); }, [load]);

  const expected = summary.expected_in_drawer;
  const diff = (Number(declared) || 0) - expected;
  const nonCash = summary.non_cash_collected || 0;
  const totalExpenses = summary.total_expenses || 0;
  const realTotal = (Number(declared) || 0) + totalExpenses + nonCash;
  const alreadyClosed = !!summary.existing_closing;

  const [expAmount, setExpAmount] = useState("");
  const [expCategory, setExpCategory] = useState("Lessive / Produits");
  const [expNote, setExpNote] = useState("");

  const addExpense = async () => {
    if (expAmount === "" || Number(expAmount) <= 0) return toast.error("Saisissez un montant de dépense valide");
    try {
      await api.post("/cash-expenses", { amount: Number(expAmount), category: expCategory, note: expNote, date: selDate });
      toast.success("Sortie de caisse enregistrée");
      setExpAmount(""); setExpNote("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delExpense = async (id) => {
    if (!(await confirm({ title: "Supprimer la dépense", description: "Supprimer cette sortie de caisse ?", confirmText: "Supprimer", danger: true }))) return;
    try { await api.delete(`/cash-expenses/${id}`); toast.success("Dépense supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const shiftDate = (dir) => {
    const d = new Date(selDate + "T00:00:00");
    d.setDate(d.getDate() + dir);
    const s = d.toISOString().slice(0, 10);
    if (s <= todayStr) setSelDate(s);
  };

  const buildCashReport = () => {
    const cname = settings?.company_name || "WuraPro";
    const dstr = new Date(selDate + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const money = (n) => fmtMoney(n || 0, cur);
    const L = [];
    L.push(`🧾 ${cname} — Caisse du ${dstr}`);
    L.push("");
    L.push(`💵 Espèces encaissées : ${money(summary.expected_cash)}`);
    if ((summary.expenses || []).length > 0) {
      L.push(`➖ Sorties de caisse : ${money(summary.total_expenses)}`);
      summary.expenses.forEach((e) => L.push(`   • ${e.category}${e.note ? " (" + e.note + ")" : ""} : ${money(e.amount)}`));
    }
    L.push(`📦 Attendu en caisse : ${money(summary.expected_in_drawer)}`);
    L.push("");
    L.push("— Détail des paiements en espèces —");
    if ((summary.cash_payments || []).length === 0) L.push("Aucun paiement en espèces");
    else summary.cash_payments.forEach((p) => L.push(`• ${p.order_number} ${p.customer_name || ""} : ${money(p.amount)}${p.agent_name ? " (" + p.agent_name + ")" : ""}`));
    const nonCashMethods = (summary.by_method || []).filter((m) => !["espèces", "especes", "cash", "liquide"].includes((m.method || "").toLowerCase()));
    if (nonCashMethods.length > 0) {
      L.push("");
      L.push("— Autres moyens —");
      nonCashMethods.forEach((m) => L.push(`• ${m.method} : ${money(m.amount)}`));
    }
    L.push("");
    L.push(`✅ Total encaissé : ${money(summary.total_collected)}`);
    if (summary.existing_closing) {
      const c = summary.existing_closing;
      L.push(`🔒 Compté : ${money(c.declared_cash)} · Écart : ${money(c.difference)}`);
      L.push(`💰 Réel comptabilisé : ${money(c.real_total)}`);
    }
    return L.join("\n");
  };

  const shareCash = async () => {
    const text = buildCashReport();
    try {
      if (navigator.share) await navigator.share({ title: `Caisse ${dateLabel}`, text });
      else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) { /* partage annulé */ }
  };

  const copyCash = async () => {
    try { await navigator.clipboard.writeText(buildCashReport()); toast.success("Détail copié — collez-le où vous voulez"); }
    catch { toast.error("Copie impossible"); }
  };

  const buildDayReport = () => {
    const cname = settings?.company_name || "WuraPro";
    const dstr = new Date(selDate + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const money = (n) => fmtMoney(n || 0, cur);
    const L = [];
    L.push(`📊 ${cname} — Récap de fin de journée`);
    L.push(`🗓️ ${dstr}`);
    L.push("");
    L.push(`✅ Total encaissé : ${money(summary.total_collected)}`);
    if ((summary.by_method || []).length > 0) {
      L.push("— Encaissements par moyen —");
      summary.by_method.forEach((m) => L.push(`• ${m.method} : ${money(m.amount)} (${m.count})`));
    }
    L.push("");
    L.push(`💵 Espèces encaissées : ${money(summary.expected_cash)}`);
    L.push(`💳 Autres moyens : ${money(summary.non_cash_collected)}`);
    if ((summary.expenses || []).length > 0) {
      L.push("");
      L.push(`➖ Sorties de caisse : ${money(summary.total_expenses)}`);
      summary.expenses.forEach((e) => L.push(`   • ${e.category}${e.note ? " (" + e.note + ")" : ""} : ${money(e.amount)}`));
    }
    L.push("");
    L.push(`📦 Attendu en caisse : ${money(summary.expected_in_drawer)}`);
    if (summary.existing_closing) {
      const c = summary.existing_closing;
      L.push(`🔒 Compté : ${money(c.declared_cash)} · Écart : ${money(c.difference)}`);
      L.push(`💰 Réel comptabilisé : ${money(c.real_total)}`);
    } else {
      L.push("⚠️ Journée non encore clôturée");
    }
    if ((summary.by_agent || []).length > 0) {
      L.push("");
      L.push("— Détail par agent —");
      summary.by_agent.forEach((a) => L.push(`• ${a.agent_name} : ${money(a.total)} (esp. ${money(a.cash)} / autres ${money(a.non_cash)})`));
    }
    if (outstanding.count > 0) {
      L.push("");
      L.push(`📌 Restes à encaisser : ${outstanding.count} commande(s) · ${money(outstanding.total_remaining)}`);
    }
    return L.join("\n");
  };

  const shareDay = async () => {
    const text = buildDayReport();
    try {
      if (navigator.share) await navigator.share({ title: `Récap caisse ${dateLabel}`, text });
      else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch (e) { /* annulé */ }
  };

  const copyDay = async () => {
    try { await navigator.clipboard.writeText(buildDayReport()); toast.success("Récap copié — collez-le où vous voulez"); }
    catch { toast.error("Copie impossible"); }
  };


  const openPay = (o) => { setPayOrder(o); setPayAmount(String(o.remaining)); setPayMethod(methods[0]?.name || "Espèces"); };
  const doPay = async () => {
    if (payAmount === "" || Number(payAmount) <= 0) return toast.error("Montant invalide");
    try {
      await api.post(`/orders/${payOrder.id}/payment`, { amount: Number(payAmount), method: payMethod });
      toast.success("Paiement encaissé");
      setPayOrder(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openCash = async () => {
    if (openingInput === "") return toast.error("Saisissez le fond de caisse");
    try {
      await api.post("/cash-closings/opening", { opening_float: Number(openingInput) });
      toast.success("Caisse ouverte");
      setOpeningInput("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    if (declared === "") return toast.error("Saisissez le montant compté en caisse");
    try {
      await api.post("/cash-closings", { declared_cash: Number(declared), note, date: selDate });
      toast.success(alreadyClosed ? "Clôture corrigée" : "Clôture enregistrée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delHistory = async (id) => {
    if (!(await confirm({ title: "Supprimer la clôture", description: "Supprimer cette ligne d'historique de clôture ?", confirmText: "Supprimer", danger: true }))) return;
    try {
      await api.delete(`/cash-closings/${id}`);
      toast.success("Clôture supprimée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (h) => {
    setEditRow(h);
    setEditDeclared(String(h.declared_cash ?? ""));
    setEditNote(h.note || "");
  };

  const saveEdit = async () => {
    if (editDeclared === "") return toast.error("Saisissez le montant compté");
    try {
      await api.put(`/cash-closings/${editRow.id}`, { declared_cash: Number(editDeclared), note: editNote });
      toast.success("Clôture corrigée");
      setEditRow(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const editDiff = editRow ? (Number(editDeclared) || 0) - (editRow.expected_cash || 0) : 0;

  return (
    <div className="space-y-5 max-w-4xl">
      <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Wallet className="text-sky-600" /> Caisse</h1>

      <Card className="p-4 bg-sky-50 border-sky-100 flex gap-3">
        <Info size={20} className="text-sky-600 shrink-0 mt-0.5" />
        <p className="text-sm text-sky-900">
          <b>Clôture générale</b> : une seule clôture par jour pour <b>toute la caisse</b> (tous les agents réunis). Comptez les <b>espèces réellement présentes</b> (hors fond d'ouverture) puis saisissez le total.
          Le <b>montant réel comptabilisé</b> (espèces comptées + autres moyens) <b>fait foi</b> dans les rapports. L'écart (compté − attendu) reste tracé et justifiable.
          {" "}Vous pouvez sélectionner une <b>date passée</b> pour rattraper ou corriger une journée.
        </p>
      </Card>

      {(
        <Card className="p-4 border-sky-200 bg-white" data-testid="cash-date-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-slate-700 font-medium"><CalendarDays size={18} className="text-sky-600" /> Journée :</div>
            <Button size="sm" variant={isToday ? "default" : "outline"} data-testid="cash-today-btn" onClick={() => setSelDate(todayStr)} className={isToday ? "bg-sky-600 hover:bg-sky-700" : ""}>Aujourd'hui</Button>
            <div className="flex gap-1">
              <Button size="icon" variant="outline" data-testid="cash-prev-day" onClick={() => shiftDate(-1)}><ChevronLeft size={16} /></Button>
              <Button size="icon" variant="outline" data-testid="cash-next-day" onClick={() => shiftDate(1)} disabled={isToday}><ChevronRight size={16} /></Button>
            </div>
            <Input type="date" data-testid="cash-date-input" value={selDate} max={todayStr} onChange={(e) => e.target.value && setSelDate(e.target.value)} className="w-auto" />
            <span className="text-sm text-slate-500 capitalize">{dateLabel}</span>
            {alreadyClosed && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full" data-testid="already-closed-badge">Déjà clôturée · réel {fmtMoney(summary.existing_closing.real_total, cur)}</span>}
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" data-testid="copy-day-btn" onClick={copyDay}><Copy size={15} className="mr-1" /> Copier</Button>
              <Button size="sm" data-testid="share-day-btn" onClick={shareDay} className="bg-sky-600 hover:bg-sky-700"><Share2 size={15} className="mr-1" /> Récap du jour</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Opening */}
      {isToday && (
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><DoorOpen size={18} className="text-amber-500" /> Ouverture de caisse</h3>
        {summary.opened ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Fond de caisse du jour</p>
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-800" data-testid="opening-float-value">{fmtMoney(summary.opening_float, cur)}</span>
              <Button variant="outline" size="sm" data-testid="edit-opening-btn" onClick={() => setSummary({ ...summary, opened: false })}>Modifier</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label>Fond de caisse (montant de départ)</Label><Input data-testid="opening-float-input" type="number" value={openingInput} onChange={(e) => setOpeningInput(e.target.value)} placeholder="ex: 10000" /></div>
            <Button data-testid="open-cash-btn" onClick={openCash} className="bg-amber-500 hover:bg-amber-600">Ouvrir la caisse</Button>
          </div>
        )}
      </Card>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-5">
          <Card className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Fond d'ouverture</p>
                <p className="text-base font-bold text-amber-700" data-testid="stat-opening">{fmtMoney(summary.opening_float, cur)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Espèces encaissées (ventes)</p>
                <p className="text-base font-bold text-slate-700" data-testid="cash-sales">{fmtMoney(summary.expected_cash || 0, cur)}</p>
              </div>
              <div className="bg-rose-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Sorties (dépenses)</p>
                <p className="text-base font-bold text-rose-700" data-testid="stat-expenses">− {fmtMoney(totalExpenses, cur)}</p>
              </div>
              <div className="bg-sky-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Attendu en caisse</p>
                <p className="text-base font-bold text-sky-700" data-testid="cash-expected">{fmtMoney(expected, cur)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Compté</p>
                <p className="text-base font-bold text-emerald-700" data-testid="stat-counted">{fmtMoney(Number(declared) || 0, cur)}</p>
              </div>
              <div className={`rounded-lg p-3 ${diff === 0 ? "bg-slate-50" : diff > 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                <p className="text-xs text-slate-500">Écart</p>
                <p className={`text-base font-bold ${diff === 0 ? "text-slate-700" : diff > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="cash-diff">{fmtMoney(diff, cur)}</p>
              </div>
            </div>
            <div><Label>Montant compté dans le tiroir</Label><Input data-testid="cash-declared" type="number" value={declared} onChange={(e) => setDeclared(e.target.value)} placeholder="Comptez l'argent puis saisissez le total" /></div>
            <div className="rounded-lg p-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white" data-testid="real-total-box">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-xs">Montant réel comptabilisé de la journée</p>
                  <p className="text-[11px] text-white/50">Espèces comptées + sorties + autres moyens (CA encaissé, fait foi)</p>
                </div>
                <p className="text-2xl font-bold" data-testid="real-total-value">{fmtMoney(realTotal, cur)}</p>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-white/60">
                <span>Comptées : {fmtMoney(Number(declared) || 0, cur)}</span>
                <span>Sorties : {fmtMoney(totalExpenses, cur)}</span>
                <span>Autres moyens : {fmtMoney(nonCash, cur)}</span>
              </div>
            </div>
            <div><Label>Note / justification de l'écart (optionnel)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: arrondi, espèces utilisées pour un achat (lessive)..." /></div>
            <p className="text-xs text-slate-400">Clôture générale · {isToday ? new Date().toLocaleString("fr-FR") : dateLabel}</p>
            <Button data-testid="submit-cash-btn" onClick={submit} className="w-full bg-sky-600 hover:bg-sky-700">{alreadyClosed ? "Corriger la clôture" : "Clôturer la caisse"} ({dateLabel})</Button>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3">Encaissements par moyen · {dateLabel}</h3>
            {summary.by_method.length === 0 ? <p className="text-slate-400 text-sm">Aucun encaissement ce jour</p> : (
              <div className="space-y-2 text-sm">
                {summary.by_method.map((m) => (
                  <div key={m.method} className="flex justify-between items-center">
                    <span className="text-slate-600">{m.method} <span className="text-slate-400">({m.count})</span></span>
                    <span className="font-medium">{fmtMoney(m.amount, cur)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold">
                  <span>Total encaissé</span><span className="text-emerald-600">{fmtMoney(summary.total_collected, cur)}</span>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Users size={18} className="text-indigo-600" /> Détail par agent · {dateLabel}</h3>
            {(summary.by_agent || []).length === 0 ? <p className="text-slate-400 text-sm">Aucun encaissement ce jour</p> : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                    <th className="px-3 py-2">Agent</th><th className="px-3 py-2 num">Espèces</th><th className="px-3 py-2 num">Autres</th><th className="px-3 py-2 num">Total</th>
                  </tr></thead>
                  <tbody>
                    {summary.by_agent.map((a) => (
                      <tr key={a.agent_name} className="border-t border-slate-100" data-testid={`agent-row-${a.agent_name}`}>
                        <td className="px-3 py-2">{a.agent_name}</td>
                        <td className="px-3 py-2 num">{fmtMoney(a.cash, cur)}</td>
                        <td className="px-3 py-2 num">{fmtMoney(a.non_cash, cur)}</td>
                        <td className="px-3 py-2 num font-semibold">{fmtMoney(a.total, cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Receipt size={18} className="text-rose-600" /> Sorties de caisse · {dateLabel}</h3>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div className="w-28"><Label className="text-xs">Montant</Label><Input data-testid="expense-amount" type="number" value={expAmount} onChange={(e) => setExpAmount(e.target.value)} placeholder="ex: 500" /></div>
              <div className="flex-1 min-w-[130px]">
                <Label className="text-xs">Catégorie</Label>
                <select data-testid="expense-category" value={expCategory} onChange={(e) => setExpCategory(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  {EXPENSE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[130px]"><Label className="text-xs">Motif (optionnel)</Label><Input data-testid="expense-note" value={expNote} onChange={(e) => setExpNote(e.target.value)} placeholder="ex: achat lessive" /></div>
              <Button data-testid="add-expense-btn" onClick={addExpense} className="bg-rose-600 hover:bg-rose-700"><Plus size={16} className="mr-1" /> Ajouter</Button>
            </div>
            {(summary.expenses || []).length === 0 ? <p className="text-slate-400 text-sm">Aucune sortie de caisse ce jour</p> : (
              <div className="space-y-1.5 text-sm max-h-56 overflow-y-auto">
                {summary.expenses.map((e) => (
                  <div key={e.id} className="flex justify-between items-center py-1 border-b border-slate-50" data-testid={`expense-row-${e.id}`}>
                    <div>
                      <span className="font-medium text-slate-700">{e.category}</span>
                      {e.note && <span className="text-slate-400 ml-2">{e.note}</span>}
                      <span className="text-slate-300 ml-2 text-xs">· {e.agent_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-rose-700">− {fmtMoney(e.amount, cur)}</span>
                      <button data-testid={`delete-expense-${e.id}`} onClick={() => delExpense(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold">
                  <span>Total sorties</span><span className="text-rose-700">− {fmtMoney(totalExpenses, cur)}</span>
                </div>
              </div>
            )}
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Banknote size={18} className="text-emerald-600" /> Détail des espèces · {dateLabel}</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="copy-cash-btn" onClick={copyCash}><Copy size={15} className="mr-1" /> Copier</Button>
              <Button size="sm" data-testid="share-cash-btn" onClick={shareCash} className="bg-emerald-600 hover:bg-emerald-700"><Share2 size={15} className="mr-1" /> Partager</Button>
            </div>
          </div>
          {summary.cash_payments.length === 0 ? <p className="text-slate-400 text-sm">Aucun paiement en espèces ce jour</p> : (
            <div className="space-y-1.5 text-sm max-h-80 overflow-y-auto">
              {summary.cash_payments.map((p, i) => (
                <div key={i} className="flex justify-between items-center py-1 border-b border-slate-50">
                  <div>
                    <span className="font-medium text-slate-700">{p.order_number}</span>
                    <span className="text-slate-400 ml-2">{p.customer_name}</span>
                    {p.agent_name && <span className="text-slate-300 ml-2 text-xs">· {p.agent_name}</span>}
                  </div>
                  <span className="font-medium">{fmtMoney(p.amount, cur)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 text-slate-500"><span>Fond d'ouverture (info)</span><span>{fmtMoney(summary.opening_float, cur)}</span></div>
              <div className="flex justify-between font-semibold"><span>Total espèces encaissées</span><span className="text-sky-700">{fmtMoney(expected, cur)}</span></div>
            </div>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden" data-testid="outstanding-card">
        <div className="flex items-center justify-between px-4 pt-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Wallet size={18} className="text-rose-600" /> Restes à encaisser (créances)</h3>
          <span className="text-sm font-semibold text-rose-700" data-testid="outstanding-total">{outstanding.count} · {fmtMoney(outstanding.total_remaining, cur)}</span>
        </div>
        <p className="text-xs text-slate-400 px-4 pt-1">Commandes non soldées. Un paiement encaissé ici est compté dans la caisse du jour où il est réglé.</p>
        <div className="overflow-x-auto mt-2">
          <table className="data-table w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">N°</th><th className="px-4 py-3">Client</th>
                <th className="px-4 py-3 num">Total</th><th className="px-4 py-3 num">Payé</th><th className="px-4 py-3 num">Reste</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {outstanding.orders.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Aucune créance 🎉</td></tr>}
              {outstanding.orders.map((o) => (
                <tr key={o.id} data-testid={`outstanding-row-${o.id}`}>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-slate-700">{o.order_number}</td>
                  <td className="px-4 py-3">{o.customer_name}{o.customer_phone && <span className="text-slate-400 text-xs ml-1">· {o.customer_phone}</span>}</td>
                  <td className="px-4 py-3 num">{fmtMoney(o.total, cur)}</td>
                  <td className="px-4 py-3 num text-emerald-600">{fmtMoney(o.paid, cur)}</td>
                  <td className="px-4 py-3 num font-semibold text-rose-700">{fmtMoney(o.remaining, cur)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" data-testid={`collect-btn-${o.id}`} onClick={() => openPay(o)} className="bg-emerald-600 hover:bg-emerald-700">Encaisser</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!payOrder} onOpenChange={(v) => !v && setPayOrder(null)}>
        <DialogContent data-testid="collect-dialog">
          <DialogHeader><DialogTitle>Encaisser un paiement</DialogTitle></DialogHeader>
          {payOrder && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><b>{payOrder.order_number}</b> · {payOrder.customer_name}</p>
                <p className="text-slate-500">Total {fmtMoney(payOrder.total, cur)} · Payé {fmtMoney(payOrder.paid, cur)} · Reste <b className="text-rose-700">{fmtMoney(payOrder.remaining, cur)}</b></p>
              </div>
              <div><Label>Montant à encaisser</Label><Input data-testid="collect-amount" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} /></div>
              <div>
                <Label>Moyen de paiement</Label>
                <select data-testid="collect-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                  {methods.map((m) => <option key={m.id || m.name} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOrder(null)}>Annuler</Button>
            <Button data-testid="confirm-collect-btn" onClick={doPay} className="bg-emerald-600 hover:bg-emerald-700">Encaisser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="overflow-hidden">
        <h3 className="font-semibold text-slate-800 px-4 pt-4">Historique des clôtures</h3>
        <div className="overflow-x-auto">
          <table className="data-table w-full text-sm mt-2">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Clôturé par</th>
              <th className="px-4 py-3 num">Ouverture</th><th className="px-4 py-3 num">Encaissé</th>
              <th className="px-4 py-3 num">Attendu</th><th className="px-4 py-3 num">Compté</th><th className="px-4 py-3 num">Écart</th><th className="px-4 py-3 num">Réel comptabilisé</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((h) => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(h.created_at)}</td>
                  <td className="px-4 py-3">{h.agent_name}</td>
                  <td className="px-4 py-3 num">{fmtMoney(h.opening_float || 0, cur)}</td>
                  <td className="px-4 py-3 num">{fmtMoney(h.cash_collected ?? h.expected_cash, cur)}</td>
                  <td className="px-4 py-3 num">{fmtMoney(h.expected_in_drawer ?? h.expected_cash, cur)}</td>
                  <td className="px-4 py-3 num">{fmtMoney(h.declared_cash, cur)}</td>
                  <td className={`px-4 py-3 num font-medium ${h.difference === 0 ? "text-emerald-600" : h.difference > 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(h.difference, cur)}</td>
                  <td className="px-4 py-3 num font-semibold text-amber-700">{fmtMoney(h.real_total ?? ((h.declared_cash || 0) + (h.non_cash_collected || 0)), cur)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-closing-${h.id}`} onClick={() => openEdit(h)} className="text-slate-400 hover:text-sky-600 mr-3"><Pencil size={16} /></button>
                    <button data-testid={`delete-closing-${h.id}`} onClick={() => delHistory(h.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400">Aucune clôture</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-2"><Pagination page={page} total={history.length} onPage={setPage} /></div>
      </Card>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent data-testid="edit-closing-dialog">
          <DialogHeader><DialogTitle>Corriger la clôture</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Attendu (espèces)</p><p className="font-bold text-slate-700">{fmtMoney(editRow.expected_cash, cur)}</p></div>
                <div className={`rounded-lg p-3 ${editDiff === 0 ? "bg-slate-50" : editDiff > 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                  <p className="text-xs text-slate-500">Nouvel écart</p>
                  <p className={`font-bold ${editDiff === 0 ? "text-slate-700" : editDiff > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="edit-diff">{fmtMoney(editDiff, cur)}</p>
                </div>
              </div>
              <div><Label>Montant compté</Label><Input data-testid="edit-declared" type="number" value={editDeclared} onChange={(e) => setEditDeclared(e.target.value)} /></div>
              <div><Label>Note / justification de l'écart</Label><Input data-testid="edit-note" value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="ex: manque justifié, erreur de comptage..." /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Annuler</Button>
            <Button data-testid="save-edit-closing" onClick={saveEdit} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
